import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { PopoverInteractionKind } from '@blueprintjs/core';
import { FiGlobe, FiLock } from 'react-icons/fi';
import { Box, Flex, Icon, Link, Popover, Text } from 'core/components';
import { CLOUD_PROVIDERS } from 'app/util/constants';
import $syn from 'app/stores/synthetics/$syn';
import { AgentCollection } from 'app/stores/synthetics/synthTypes';
import AgentPopoverCard from 'app/views/synthetics/components/AgentPopoverCard';
import { useSynthAgentDrawer } from 'app/views/synthetics/components/agentDrawer/synthAgentDrawerContext';

/** Which left icon(s) to show. Rollout preset uses cloud only. */
export type AgentIconMode = 'none' | 'cloud' | 'type' | 'cloudThenType' | 'cloudOrType';

/** Minimal shape of the (untyped JS) AgentModel this component reads. */
interface AgentLike {
  id?: string | number;
  get(field?: string): unknown;
  label?: string;
  asFormatted?: string;
  status?: { icon?: string; color?: string; text?: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: React.ComponentType<any>;
  cloudRegion?: string;
  locationString?: string | null;
}

export interface AgentDisplayProps {
  /** MobX agent model from $syn.agents. Prefer passing this when you have it. */
  agent?: AgentLike | null;
  /** Agent id — looked up via $syn when `agent` is not provided. */
  agentId?: string | number;

  // Content toggles
  icon?: AgentIconMode; // default 'cloud'
  showCloudRegion?: boolean;
  showAsn?: boolean;
  showStatus?: boolean;
  showLocation?: boolean;

  // Presentation
  align?: 'inline' | 'spaceBetween'; // ASN pushed right when 'spaceBetween'
  /** Where the cloud/type icon sits: before the name, or grouped with the ASN on the right. */
  iconPosition?: 'left' | 'withAsn';
  size?: 'small' | 'normal';
  compact?: boolean;
  clickable?: boolean;
  linkTo?: string;
  /** Wrap in a hover popover showing an agent summary card (site/labels/asn/ips). */
  withPopover?: boolean;
  /** Show an "Open details" action in the popover that opens the synth agent side panel. */
  withDetails?: boolean;
  /** Make the whole row clickable to open the synth agent side panel (no navigation). */
  openDetailsOnClick?: boolean;
}

const ICON_BOX_CSS = { '& svg': { height: '14px', width: 'auto' } } as const;

function CloudOrTypeIcon({ agent, mode }: { agent: AgentLike; mode: AgentIconMode }) {
  if (mode === 'none') {
    return null;
  }

  const provider = agent.get('metadata.cloud_provider') as string | undefined;
  const cloudLogo = provider ? CLOUD_PROVIDERS.byId(provider).logo : null;

  // One icon, cloud preferred: a globe for global agents, a padlock for private ones.
  if (mode === 'cloudOrType') {
    const isGlobal = agent.get('agent_type') === 'global';
    const content = cloudLogo ?? (
      <Icon
        icon={isGlobal ? FiGlobe : FiLock}
        iconSize={14}
        color="muted"
        title={isGlobal ? 'Global agent' : 'Private agent'}
      />
    );

    return (
      <Flex alignItems="center" flex="0 0 auto" mr="6px" overflow="hidden" maxWidth="22px" css={ICON_BOX_CSS}>
        {content}
      </Flex>
    );
  }

  const wantsCloud = mode === 'cloud' || mode === 'cloudThenType';
  const wantsType = mode === 'type' || mode === 'cloudThenType';

  const TypeIcon = agent.icon;
  const typeEl = wantsType && TypeIcon ? <TypeIcon width={14} height={14} /> : null;
  const cloudEl = wantsCloud && cloudLogo ? cloudLogo : null;

  const content = cloudEl || typeEl;
  if (!content) {
    return null;
  }

  return (
    <Flex alignItems="center" flex="0 0 auto" mr="6px" overflow="hidden" maxWidth="22px" css={ICON_BOX_CSS}>
      {content}
      {mode === 'cloudThenType' && cloudEl && typeEl && <Box ml="4px">{typeEl}</Box>}
    </Flex>
  );
}

/**
 * Single, configurable agent renderer used across UDE + synthetics surfaces
 * (legend table, agent filter rows/selection, agent links). Rollout preset:
 * cloud icon left (if any) + name + long-form ASN pushed right.
 */
const AgentDisplay = observer((props: AgentDisplayProps) => {
  const {
    agent: agentProp,
    agentId,
    icon = 'cloud',
    showCloudRegion = false,
    showAsn = false,
    showStatus = false,
    showLocation = false,
    align = 'inline',
    iconPosition = 'left',
    size = 'small',
    compact = false,
    clickable = false,
    linkTo,
    withPopover = false,
    withDetails = false,
    openDetailsOnClick = false
  } = props;

  const drawer = useSynthAgentDrawer();

  // Resolve the agent from the store when only an id is supplied.
  const synAgents = $syn.agents as unknown as AgentCollection | null;
  useEffect(() => {
    if (!agentProp && synAgents && !synAgents.hasFetched) {
      synAgents.fetch();
    }
  }, [agentProp, synAgents]);

  let agent = agentProp ?? null;
  if (!agent && agentId != null && synAgents) {
    const result = synAgents.get(agentId);
    agent = ((Array.isArray(result) ? result[0] : result) as AgentLike | undefined) ?? null;
  }

  const small = size === 'small';

  if (!agent) {
    return (
      <Text small={small} muted>
        {agentId != null ? `Agent ${agentId}` : 'Unknown agent'}
      </Text>
    );
  }

  const asn = showAsn ? (agent.get('asn') as string | number | undefined) : undefined;
  const asnText = asn && String(asn) !== '0' ? agent.asFormatted : null;

  const provider = agent.get('metadata.cloud_provider') as string | undefined;
  const cloudRegion = showCloudRegion && provider ? agent.cloudRegion : null;
  const location = showLocation ? agent.locationString : null;
  const status = showStatus ? agent.status : null;

  const content = (
    <Flex alignItems="center" overflow="hidden" width="100%" py={compact ? '1px' : '3px'}>
      {iconPosition === 'left' && <CloudOrTypeIcon agent={agent} mode={icon} />}
      <Text small={small} ellipsis flex="1 1 auto" minWidth={0}>
        {agent.label ?? `Agent ${agent.id ?? ''}`}
      </Text>
      {status?.icon && <Icon ml="4px" icon={status.icon} color={status.color} title={status.text} iconSize={12} />}
      {(cloudRegion || location) && (
        <Text as="span" ml="6px" muted small style={{ whiteSpace: 'nowrap', opacity: 0.6 }}>
          {cloudRegion || location}
        </Text>
      )}
      {(asnText || iconPosition === 'withAsn') && (
        <Flex
          alignItems="center"
          ml={align === 'spaceBetween' ? 'auto' : '6px'}
          pl={align === 'spaceBetween' ? '8px' : 0}
          minWidth={0}
          // Fixed basis when right-aligned so every row's provider info starts at the same x.
          flex={align === 'spaceBetween' ? '0 0 45%' : '0 1 auto'}
        >
          {iconPosition === 'withAsn' && <CloudOrTypeIcon agent={agent} mode={icon} />}
          {asnText && (
            <Text as="span" ellipsis muted small minWidth={0} style={{ opacity: 0.6 }}>
              {asnText}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );

  const opensDetails = openDetailsOnClick && drawer.canOpen && agent.id != null;
  let rendered = content;

  if (opensDetails) {
    const detailsAgentId = agent.id as string | number;
    // Stop the click/key from bubbling to an outer row handler (e.g. the tests table row,
    // which would also open the test drawer).
    const openDetails = (e: React.SyntheticEvent) => {
      e.stopPropagation();
      drawer.openAgentDrawer(detailsAgentId);
    };
    rendered = (
      <div
        onClick={openDetails}
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            openDetails(e);
          }
        }}
      >
        {content}
      </div>
    );
  }

  if (withPopover) {
    const canOpenDetails = withDetails && drawer.canOpen && agent.id != null;
    const fullPageHref =
      withDetails && !drawer.canOpen && agent.id != null
        ? (linkTo ?? `/v4/synthetics/agents/${agent.id}/summary`)
        : undefined;
    return (
      <Popover
        interactionKind={PopoverInteractionKind.HOVER}
        hoverOpenDelay={200}
        minimal={false}
        position="bottom"
        boundary="viewport"
        content={
          <AgentPopoverCard
            agent={agent}
            onOpenDetails={canOpenDetails ? () => drawer.openAgentDrawer(agent.id as string | number) : undefined}
            fullPageHref={fullPageHref}
          />
        }
      >
        {rendered}
      </Popover>
    );
  }

  if (!opensDetails && clickable && agent.id != null) {
    const href = linkTo ?? `/v4/synthetics/agents/${agent.id}/summary`;
    return (
      <Link to={href} style={{ color: 'inherit', textDecoration: 'none', display: 'block' }}>
        {content}
      </Link>
    );
  }

  return rendered;
});

export default AgentDisplay;

/** Cloud icon left (if any) + name + long-form ASN pushed right — the first rollout look. */
export const AGENT_PRESET_COMPACT_ASN: Partial<AgentDisplayProps> = {
  icon: 'cloud',
  showAsn: true,
  align: 'spaceBetween',
  size: 'small',
  compact: true
};

/** Cloud icon left (if any) + name. For rows that pair with a popover, where the ASN is a click away. */
export const AGENT_PRESET_COMPACT: Partial<AgentDisplayProps> = {
  icon: 'cloud',
  size: 'small',
  compact: true
};

/** Name left, cloud icon + long-form ASN as a right-hand column. */
export const AGENT_PRESET_ASN_COLUMN: Partial<AgentDisplayProps> = {
  icon: 'cloud',
  showAsn: true,
  align: 'spaceBetween',
  iconPosition: 'withAsn',
  size: 'small',
  compact: true
};
